import type { SbefCode } from './sbef.model';

export type TypbeteckningEntry = {
  /** Mellankategori — B → Balk, YV → Yttervägg. */
  name: string;
  /** Full underkategori; disambiguates codes that share `name`. */
  underkategori: string;
  sbefCode: SbefCode;
};

/**
 * CoClass typbeteckningar (Bygg) keyed by typcode.
 * `name` is the mellankategori used as the stable display label.
 * `sbefCode` is the BSAB83/SBEF two-digit mapping from the source table.
 */
export const TYPBETECKNING_BY_CODE = {
  BB: {
    name: 'Balk',
    underkategori: 'Balk, betong',
    sbefCode: '34',
  },
  BJL: {
    name: 'Bjälklag',
    underkategori: 'Bjälklag',
    sbefCode: '34',
  },
  BL: {
    name: 'Balk',
    underkategori: 'Balk, limträ',
    sbefCode: '34',
  },
  BS: {
    name: 'Balk',
    underkategori: 'Balk, stål',
    sbefCode: '34',
  },
  BT: {
    name: 'Balk',
    underkategori: 'Balk, element av trä',
    sbefCode: '34',
  },
  B: {
    name: 'Balk',
    underkategori: 'Balk',
    sbefCode: '34',
  },
  IVB: {
    name: 'Innervägg',
    underkategori: 'Innervägg, bärande',
    sbefCode: '31',
  },
  PB: {
    name: 'Pelare',
    underkategori: 'Pelare, betong',
    sbefCode: '32',
  },
  PL: {
    name: 'Pelare',
    underkategori: 'Pelare, limträ',
    sbefCode: '32',
  },
  PS: {
    name: 'Pelare',
    underkategori: 'Pelare, stål',
    sbefCode: '32',
  },
  PT: {
    name: 'Pelare',
    underkategori: 'Pelare, element av trä',
    sbefCode: '32',
  },
  P: {
    name: 'Pelare',
    underkategori: 'Pelare',
    sbefCode: '32',
  },
  STA: {
    name: 'Stag',
    underkategori: 'Stag, lina',
    sbefCode: '38',
  },
  STR: {
    name: 'Sträva',
    underkategori: 'Sträva, stötta',
    sbefCode: '38',
  },
  TKS: {
    name: 'Takstol',
    underkategori: 'Takstol',
    sbefCode: '41',
  },
  TKY: {
    name: 'Yttertaks',
    underkategori: 'Yttertaks- och ytterbjälklagsstommar',
    sbefCode: '41',
  },
  TRS: {
    name: 'Trappa',
    underkategori: 'Trappa, stomme',
    sbefCode: '36',
  },
  TRT: {
    name: 'Trappa',
    underkategori: 'Trappa, terräng',
    sbefCode: '19',
  },
  FU: {
    name: 'Fundament',
    underkategori: 'Fundament',
    sbefCode: '24',
  },
  GB: {
    name: 'Grundbalk',
    underkategori: 'Grundbalk, kantbalk',
    sbefCode: '24',
  },
  GM: {
    name: 'Grundmur',
    underkategori: 'Grundmur',
    sbefCode: '24',
  },
  GPL: {
    name: 'Grundplatta',
    underkategori: 'Grundplatta',
    sbefCode: '27',
  },
  GRP: {
    name: 'Grop',
    underkategori: 'Grop',
    sbefCode: '24',
  },
  KOS: {
    name: 'Konstruktiv',
    underkategori: 'Konstruktiv sockel',
    sbefCode: '27',
  },
  PEG: {
    name: 'Prefabelement',
    underkategori: 'Prefabelement för gjutning',
    sbefCode: '39',
  },
  PH: {
    name: 'Pelarholk',
    underkategori: 'Pelarholk',
    sbefCode: '24',
  },
  PLB: {
    name: 'Påle',
    underkategori: 'Påle, betong',
    sbefCode: '13',
  },
  PLP: {
    name: 'Pålplint',
    underkategori: 'Pålplint, pålfundament',
    sbefCode: '24',
  },
  PLG: {
    name: 'Påle',
    underkategori: 'Påle',
    sbefCode: '13',
  },
  PLK: {
    name: 'Pålkrage',
    underkategori: 'Pålkrage',
    sbefCode: '24',
  },
  PLS: {
    name: 'Påle',
    underkategori: 'Påle, stål',
    sbefCode: '13',
  },
  PLT: {
    name: 'Påle',
    underkategori: 'Påle, trä',
    sbefCode: '13',
  },
  SAB: {
    name: 'Sarg',
    underkategori: 'Sarg, bottenplatta',
    sbefCode: '27',
  },
  YKOS: {
    name: 'Konstruktiv',
    underkategori: 'Konstruktiv sockel, utvändig',
    sbefCode: '27',
  },
  BP: {
    name: 'Balkongplatta',
    underkategori: 'Balkongplatta',
    sbefCode: '68',
  },
  LP: {
    name: 'Loftgångsplatta',
    underkategori: 'Loftgångsplatta',
    sbefCode: '68',
  },
  LS: {
    name: 'Ledstång',
    underkategori: 'Ledstång',
    sbefCode: '78',
  },
  PLFF: {
    name: 'Plattform',
    underkategori: 'Plattform, fasad',
    sbefCode: '58',
  },
  PLF: {
    name: 'Plattform',
    underkategori: 'Plattform, innomhus',
    sbefCode: '68',
  },
  RA: {
    name: 'Ramp',
    underkategori: 'Ramp',
    sbefCode: '68',
  },
  RBU: {
    name: 'Balustrad',
    underkategori: 'Balustrad',
    sbefCode: '68',
  },
  RB: {
    name: 'Balkongräcke',
    underkategori: 'Balkongräcke, balkongfront, utvändigt',
    sbefCode: '58',
  },
  RT: {
    name: 'Trappräcke',
    underkategori: 'Trappräcke, invändigt',
    sbefCode: '68',
  },
  SKT: {
    name: 'Skärmtak',
    underkategori: 'Skärmtak',
    sbefCode: '58',
  },
  SKV: {
    name: 'Skärmvägg',
    underkategori: 'Skärmvägg',
    sbefCode: '78',
  },
  STF: {
    name: 'Stege',
    underkategori: 'Stege, fasad',
    sbefCode: '58',
  },
  STT: {
    name: 'Stege',
    underkategori: 'Stege, tak',
    sbefCode: '49',
  },
  TR: {
    name: 'Trappa',
    underkategori: 'Trappa, invändig',
    sbefCode: '66',
  },
  YLS: {
    name: 'Ledstång',
    underkategori: 'Ledstång, yttre',
    sbefCode: '59',
  },
  YRBU: {
    name: 'Balustrad',
    underkategori: 'Balustrad, utvändigt',
    sbefCode: '58',
  },
  YRT: {
    name: 'Trappräcke',
    underkategori: 'Trappräcke, utvändigt',
    sbefCode: '58',
  },
  YSKV: {
    name: 'Skärmvägg',
    underkategori: 'Skärmvägg, utvändig',
    sbefCode: '59',
  },
  YTR: {
    name: 'Trappa',
    underkategori: 'Trappa, utvändig',
    sbefCode: '36',
  },
  YVG: {
    name: 'Nät',
    underkategori: 'Nät och gallerväggar, utvändig',
    sbefCode: '69',
  },
  BE: {
    name: 'Beläggningar',
    underkategori: 'Beläggningar och beklädnader på väggyta',
    sbefCode: '73',
  },
  GVB: {
    name: 'Golvbeläggning',
    underkategori: 'Golvbeläggning',
    sbefCode: '72',
  },
  IG: {
    name: 'Installationsgolv',
    underkategori: 'Installationsgolv',
    sbefCode: '69',
  },
  IT: {
    name: 'Innertak',
    underkategori: 'Innertak',
    sbefCode: '64',
  },
  IVG: {
    name: 'Nät',
    underkategori: 'Nät och gallerväggar',
    sbefCode: '78',
  },
  IVI: {
    name: 'Innervägg',
    underkategori: 'Innervägg, väggskikt för installationer',
    sbefCode: '63',
  },
  IV: {
    name: 'Innervägg',
    underkategori: 'Innervägg',
    sbefCode: '63',
  },
  UG: {
    name: 'Undergolv',
    underkategori: 'Undergolv',
    sbefCode: '62',
  },
  UT: {
    name: 'Undertak',
    underkategori: 'Undertak, installationsstak',
    sbefCode: '74',
  },
  VR: {
    name: 'Vägg',
    underkategori: 'Vägg, rörlig',
    sbefCode: '63',
  },
  BJLT: {
    name: 'Terrassbjälklag',
    underkategori: 'Terrassbjälklag',
    sbefCode: '34',
  },
  YBE: {
    name: 'Beläggningar',
    underkategori: 'Beläggningar och beklädnader på del av väggyta, utvändigt',
    sbefCode: '53',
  },
  YTB: {
    name: 'Yttertak',
    underkategori: 'Yttertak - beklädnad',
    sbefCode: '43',
  },
  YTS: {
    name: 'Yttertak',
    underkategori: 'Yttertak, ytterklimatskärm',
    sbefCode: '43',
  },
  YT: {
    name: 'Yttertak',
    underkategori: 'Yttertak, sammansatt',
    sbefCode: '40',
  },
  YVB: {
    name: 'Yttervägg',
    underkategori: 'Yttervägg, bärande',
    sbefCode: '31',
  },
  YVF: {
    name: 'Yttervägg',
    underkategori: 'Yttervägg, Fasad',
    sbefCode: '50',
  },
  YVK: {
    name: 'Yttervägg',
    underkategori: 'Yttervägg i källare',
    sbefCode: '31',
  },
  YVU: {
    name: 'Yttervägg',
    underkategori: 'Yttervägg, Utfackningsvägg',
    sbefCode: '51',
  },
  YV: {
    name: 'Yttervägg',
    underkategori: 'Yttervägg, icke bärande',
    sbefCode: '51',
  },
  JR: {
    name: 'Jalusi',
    underkategori: 'Jalusi för speciell användning, invändig',
    sbefCode: '65',
  },
  YJR: {
    name: 'Jalusi',
    underkategori: 'Jalusi för speciell användning, utvändig',
    sbefCode: '55',
  },
  J: {
    name: 'Jalusi',
    underkategori: 'Jalusi, invändig',
    sbefCode: '65',
  },
  MRK: {
    name: 'Markiser',
    underkategori: 'Markiser, invändig',
    sbefCode: '78',
  },
  MR: {
    name: 'Mörkläggningsridåer',
    underkategori: 'Mörkläggningsridåer',
    sbefCode: '78',
  },
  PE: {
    name: 'Persienner',
    underkategori: 'Persienner, invändiga',
    sbefCode: '55',
  },
  RG: {
    name: 'Rullgardiner',
    underkategori: 'Rullgardiner',
    sbefCode: '78',
  },
  YJS: {
    name: 'Intrångsskydd',
    underkategori: 'Intrångsskydd',
    sbefCode: '55',
  },
  YJ: {
    name: 'Jalusi',
    underkategori: 'Jalusi, utvändig',
    sbefCode: '55',
  },
  YMRK: {
    name: 'Markiser',
    underkategori: 'Markiser, utvändig',
    sbefCode: '58',
  },
  YPE: {
    name: 'Persienner',
    underkategori: 'Persienner, utvändiga',
    sbefCode: '58',
  },
  GLSK: {
    name: 'Glidskydd',
    underkategori: 'Glidskydd, stege',
    sbefCode: '49',
  },
  GVK: {
    name: 'Gavelkomplettering',
    underkategori: 'Gavelkomplettering',
    sbefCode: '44',
  },
  HR: {
    name: 'Hängränna',
    underkategori: 'Hängränna, utvändig',
    sbefCode: '46',
  },
  LLF: {
    name: 'Livlinefäste',
    underkategori: 'Livlinefäste, fästögla',
    sbefCode: '49',
  },
  PLFT: {
    name: 'Plattform',
    underkategori: 'Plattform, tak',
    sbefCode: '48',
  },
  SKYRT: {
    name: 'Skyddsräcke',
    underkategori: 'Skyddsräcke, tak',
    sbefCode: '49',
  },
  SRS: {
    name: 'Snörasskydd',
    underkategori: 'Snörasskydd',
    sbefCode: '49',
  },
  STPR: {
    name: 'Stuprör',
    underkategori: 'Stuprör, invändig',
    sbefCode: '46',
  },
  TABR: {
    name: 'Takbrygga',
    underkategori: 'Takbrygga',
    sbefCode: '49',
  },
  TAFK: {
    name: 'Takfotskomplettering',
    underkategori: 'Takfotskomplettering',
    sbefCode: '44',
  },
  TAR: {
    name: 'Takfotsräcke',
    underkategori: 'Takfotsräcke, nockräcke',
    sbefCode: '46',
  },
  TTBR: {
    name: 'Tillträdesbrygga',
    underkategori: 'Tillträdesbrygga',
    sbefCode: '46',
  },
  UTBR: {
    name: 'Utrymningsbrygga',
    underkategori: 'Utrymningsbrygga',
    sbefCode: '46',
  },
  DF: {
    name: 'Dörrfoder',
    underkategori: 'Dörrfoder',
    sbefCode: '65',
  },
  FB: {
    name: 'Fönsterbänkar',
    underkategori: 'Fönsterbänkar',
    sbefCode: '78',
  },
  FFD: {
    name: 'Fönsterfoder',
    underkategori: 'Fönsterfoder',
    sbefCode: '55',
  },
  GS: {
    name: 'Golvsockel',
    underkategori: 'Golvsockel',
    sbefCode: '72',
  },
  PKS: {
    name: 'Påkörningsskydd',
    underkategori: 'Påkörningsskydd, avbärarlister',
    sbefCode: '78',
  },
  SB: {
    name: 'Smygbrädor',
    underkategori: 'Smygbrädor',
    sbefCode: '78',
  },
  STS: {
    name: 'Stänkskydd',
    underkategori: 'Stänkskydd',
    sbefCode: '73',
  },
  TL: {
    name: 'Taklister',
    underkategori: 'Taklister',
    sbefCode: '78',
  },
  TRL: {
    name: 'Tröskel',
    underkategori: 'Tröskel',
    sbefCode: '78',
  },
  HFB: {
    name: 'Hängställning',
    underkategori: 'Hängställning, fasadbryggor',
    sbefCode: '87',
  },
  HFV: {
    name: 'Hissfrontsvägg',
    underkategori: 'Hissfrontsvägg',
    sbefCode: '87',
  },
  HF: {
    name: 'Hissfront',
    underkategori: 'Hissfront',
    sbefCode: '87',
  },
  HLY: {
    name: 'Lyftbord',
    underkategori: 'Lyftbord, pelarlyft, fordonslyft mm',
    sbefCode: '87',
  },
  HPL: {
    name: 'Plattformshiss',
    underkategori: 'Plattformshiss',
    sbefCode: '87',
  },
  HP: {
    name: 'Personhiss',
    underkategori: 'Personhiss',
    sbefCode: '87',
  },
  HSO: {
    name: 'Soptransporthiss',
    underkategori: 'Soptransporthiss',
    sbefCode: '87',
  },
  HSG: {
    name: 'Sänghiss',
    underkategori: 'Sänghiss',
    sbefCode: '87',
  },
  HTR: {
    name: 'Trapphiss',
    underkategori: 'Trapphiss',
    sbefCode: '87',
  },
  HU: {
    name: 'Utrymningshiss',
    underkategori: 'Utrymningshiss',
    sbefCode: '87',
  },
  HV: {
    name: 'Varu',
    underkategori: 'Varu, småvaruhiss',
    sbefCode: '87',
  },
  H: {
    name: 'Hiss',
    underkategori: 'Hiss',
    sbefCode: '87',
  },
  POM: {
    name: 'Port',
    underkategori: 'Port, maskindriven',
    sbefCode: '55',
  },
  RLB: {
    name: 'Rullband',
    underkategori: 'Rullband, rullramp',
    sbefCode: '87',
  },
  RTR: {
    name: 'Rulltrappa',
    underkategori: 'Rulltrappa',
    sbefCode: '87',
  },
  SAS: {
    name: 'Solavskärmning',
    underkategori: 'Solavskärmning, maskindriven',
    sbefCode: '55',
  },
  TB: {
    name: 'Transportband',
    underkategori: 'Transportband, transportramper',
    sbefCode: '87',
  },
  TRA: {
    name: 'Kransystem',
    underkategori: 'Kransystem, travers, telfer',
    sbefCode: '87',
  },
  VRM: {
    name: 'Vägg',
    underkategori: 'Vägg, rörlig, maskindriven',
    sbefCode: '63',
  },
  YPOM: {
    name: 'Port',
    underkategori: 'Port, maskindriven, ytter',
    sbefCode: '55',
  },
  YSAS: {
    name: 'Solavskärmning',
    underkategori: 'Solavskärmning, yttre,  maskindriven',
    sbefCode: '55',
  },
  DA: {
    name: 'Dörr',
    underkategori: 'Dörr, aluminium',
    sbefCode: '65',
  },
  DS: {
    name: 'Dörr',
    underkategori: 'Dörr, stål',
    sbefCode: '65',
  },
  DT: {
    name: 'Dörr',
    underkategori: 'Dörr, trä',
    sbefCode: '65',
  },
  D: {
    name: 'Dörr',
    underkategori: 'Dörr',
    sbefCode: '65',
  },
  FD: {
    name: 'Fönsterdörr',
    underkategori: 'Fönsterdörr',
    sbefCode: '55',
  },
  FF: {
    name: 'Fönster',
    underkategori: 'Fönster, fast, ej öppningsbart',
    sbefCode: '55',
  },
  FL: {
    name: 'Fönster',
    underkategori: 'Fönster, funktion röklucka',
    sbefCode: '55',
  },
  FO: {
    name: 'Fönster',
    underkategori: 'Fönster, öppningsbart',
    sbefCode: '55',
  },
  F: {
    name: 'Fönster',
    underkategori: 'Fönster',
    sbefCode: '55',
  },
  GF: {
    name: 'Fasadgaller',
    underkategori: 'Fasadgaller',
    sbefCode: '58',
  },
  GPA: {
    name: 'Glasparti',
    underkategori: 'Glasparti, aluminium',
    sbefCode: '65',
  },
  GPS: {
    name: 'Glasparti',
    underkategori: 'Glasparti, stål',
    sbefCode: '65',
  },
  GPT: {
    name: 'Glasparti',
    underkategori: 'Glasparti, trä',
    sbefCode: '65',
  },
  GP: {
    name: 'Glasparti',
    underkategori: 'Glasparti',
    sbefCode: '65',
  },
  GR: {
    name: 'Grind',
    underkategori: 'Grind, invändig',
    sbefCode: '65',
  },
  LUB: {
    name: 'Lucka',
    underkategori: 'Lucka, bjälklag',
    sbefCode: '65',
  },
  LUI: {
    name: 'Inspektionslucka',
    underkategori: 'Inspektionslucka',
    sbefCode: '78',
  },
  LURT: {
    name: 'Rök',
    underkategori: 'Rök- och Taklucka',
    sbefCode: '45',
  },
  LUR: {
    name: 'Röklucka',
    underkategori: 'Röklucka',
    sbefCode: '45',
  },
  LUT: {
    name: 'Taklucka',
    underkategori: 'Taklucka',
    sbefCode: '45',
  },
  LU: {
    name: 'Lucka',
    underkategori: 'Lucka',
    sbefCode: '45',
  },
  PO: {
    name: 'Port',
    underkategori: 'Port, invändig',
    sbefCode: '65',
  },
  ROK: {
    name: 'Rökkanal',
    underkategori: 'Rökkanal',
    sbefCode: '85',
  },
  SKO: {
    name: 'Skorsten',
    underkategori: 'Skorsten, murad',
    sbefCode: '48',
  },
  TF: {
    name: 'Takfönster',
    underkategori: 'Takfönster',
    sbefCode: '45',
  },
  YDA: {
    name: 'Dörr',
    underkategori: 'Dörr, aluminium, utvändig',
    sbefCode: '55',
  },
  YDS: {
    name: 'Dörr',
    underkategori: 'Dörr, stål, utvändig',
    sbefCode: '55',
  },
  YDT: {
    name: 'Dörr',
    underkategori: 'Dörr, trä, utvändig',
    sbefCode: '55',
  },
  YD: {
    name: 'Dörr',
    underkategori: 'Dörr, utvändig',
    sbefCode: '55',
  },
  YGPA: {
    name: 'Glasparti',
    underkategori: 'Glasparti, aluminium, utvändig',
    sbefCode: '55',
  },
  YGPS: {
    name: 'Glasparti',
    underkategori: 'Glasparti, stål, utvändig',
    sbefCode: '55',
  },
  YGPT: {
    name: 'Glasparti',
    underkategori: 'Glasparti, trä, utvändig',
    sbefCode: '55',
  },
  YGP: {
    name: 'Glasparti',
    underkategori: 'Glasparti, utvändigt',
    sbefCode: '55',
  },
  YGR: {
    name: 'Grind',
    underkategori: 'Grind, utvändig',
    sbefCode: '55',
  },
  YLUI: {
    name: 'Inspektionslucka',
    underkategori: 'Inspektionslucka, utvändig',
    sbefCode: '58',
  },
  YLU: {
    name: 'Lucka',
    underkategori: 'Lucka, utvändig',
    sbefCode: '55',
  },
  YPO: {
    name: 'Port',
    underkategori: 'Port, ytter',
    sbefCode: '55',
  },
} as const satisfies Record<string, TypbeteckningEntry>;

export type TypbeteckningCode = keyof typeof TYPBETECKNING_BY_CODE;

export const TYPBETECKNING_CODES = Object.keys(TYPBETECKNING_BY_CODE) as [
  TypbeteckningCode,
  ...TypbeteckningCode[],
];

export const isTypbeteckningCode = (
  value: unknown,
): value is TypbeteckningCode =>
  typeof value === 'string' && Object.hasOwn(TYPBETECKNING_BY_CODE, value);

export const getTypbeteckning = (
  code: string,
): (typeof TYPBETECKNING_BY_CODE)[TypbeteckningCode] | undefined =>
  isTypbeteckningCode(code) ? TYPBETECKNING_BY_CODE[code] : undefined;
